import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import * as api from "./progressApi";

const listKey = (query: Omit<api.ProgressQuery, "cursor">) =>
  [
    "portal",
    "grand-prize-progress",
    query.status,
    query.account,
    query.search,
  ] as const;

// Una sola llamada a `portalListProgress` entrega `items` + `indicators`
// (SPEC-209 §2): los cinco indicadores de cabecera se leen de esta misma
// respuesta — nunca una segunda query.
export function useProgressList(query: Omit<api.ProgressQuery, "cursor">) {
  return useInfiniteQuery({
    queryKey: listKey(query),
    queryFn: ({ pageParam }) =>
      api.listProgress({
        ...query,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.meta.next ?? undefined,
  });
}

export const useProgressDetail = (id: string | null) =>
  useQuery({
    queryKey: ["portal", "grand-prize-progress-detail", id],
    queryFn: () => api.getProgressDetail(id!),
    enabled: Boolean(id),
  });

export function useProgressMutations() {
  const client = useQueryClient();
  const invalidate = (id?: string) => {
    void client.invalidateQueries({
      queryKey: ["portal", "grand-prize-progress"],
    });
    if (id) {
      void client.invalidateQueries({
        queryKey: ["portal", "grand-prize-progress-detail", id],
      });
    }
  };
  return {
    validateGoal: useMutation({
      mutationFn: (id: string) => api.validateGoal(id),
      onSuccess: (_data, id) => invalidate(id),
    }),
    deliverPrize: useMutation({
      mutationFn: ({
        id,
        input,
      }: {
        id: string;
        input: api.DeliverPrizeInput;
      }) => api.deliverPrize(id, input),
      onSuccess: (_data, { id }) => invalidate(id),
    }),
  };
}
